/**
 * Service Health page.
 *
 * Surfaces each backend service's currently-running git SHA against
 * the web bundle's own SHA — "did the worker deploy actually pick up
 * the commit I just merged?" without manually checking Cloud Run
 * revisions. Lifted out of AdminCommandCenter so the answer is one
 * click away from the Operations menu instead of buried inside the
 * profile-list page.
 *
 * Row states:
 *   - bootedAt unknown → service has never reported (hasn't redeployed
 *     since the schema landed). Greyed out.
 *   - lastSeenAt > 5min ago → STALE. The process is dead, redeploying,
 *     or the heartbeat write is failing.
 *   - sha matches WEB_VERSION.sha → in-sync, green chip.
 *   - sha differs → orange "behind" chip + GitHub compare link.
 */
import React from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {
  Box,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {useGetServiceHealth} from '../../api/graphql_admin_service_health';
import {WEB_VERSION} from '../../version.generated';
import {usePageTitle} from '../../hooks/usePageTitle';
import {pageSx, pageTitleSx, pcolors} from '../../theme/planapusTheme';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

function humanizeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = t - Date.now();
  const ms = Math.abs(diff);
  const past = diff < 0;
  let str: string;
  if (ms < 60_000) str = `${Math.floor(ms / 1000)}s`;
  else if (ms < 3_600_000) str = `${Math.floor(ms / 60_000)}m`;
  else if (ms < 86_400_000) str = `${Math.floor(ms / 3_600_000)}h`;
  else if (ms < 30 * 86_400_000) str = `${Math.floor(ms / 86_400_000)}d`;
  else return new Date(iso).toLocaleDateString();
  return past ? `${str} ago` : `in ${str}`;
}

const REPO_URL = 'https://github.com/a7medkamel/kidtube';

const SERVICE_LABELS: Record<string, string> = {
  'bullmq-worker': 'BullMQ worker',
  'cloud-functions': 'Cloud Functions',
};

type ServiceHealthEntry = NonNullable<
  NonNullable<ReturnType<typeof useGetServiceHealth>['data']>['getServiceHealth']
>[number];

export default function AdminServiceHealth({client}: Props) {
  usePageTitle('Admin – Service Health');
  const {data, loading} = useGetServiceHealth(client);
  const rows = data?.getServiceHealth ?? [];

  return (
    <Box sx={pageSx}>
      <Box sx={{mb: 3}}>
        <Typography sx={pageTitleSx}>Service Health</Typography>
        <Typography sx={{fontSize: 13, color: pcolors.textSecondary, mt: 0.5}}>
          Per-service heartbeat + git SHA compared against the web bundle.
        </Typography>
      </Box>

      <Box
        sx={{
          borderRadius: 1.5,
          border: '1px solid #e2e8f0',
          bgcolor: '#fff',
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: '#64748b',
            }}
          >
            Service health
          </Typography>
          <Typography sx={{fontSize: 10, color: '#94a3b8'}}>
            this UI:{' '}
            <Box
              component="a"
              href={`${REPO_URL}/commit/${WEB_VERSION.sha}`}
              target="_blank"
              rel="noreferrer"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 700,
                color: '#4f46e5',
                textDecoration: 'none',
                '&:hover': {textDecoration: 'underline'},
              }}
            >
              {WEB_VERSION.shortSha}
            </Box>
            {WEB_VERSION.dirty ? (
              <span style={{color: '#e11d48'}}> (dirty)</span>
            ) : null}
          </Typography>
        </Box>

        {loading && rows.length === 0 ? (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}>
            <CircularProgress size={18} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography
            sx={{fontSize: 12, color: '#94a3b8', fontStyle: 'italic'}}
          >
            No service has reported yet. Each backend service writes its row
            on cold start — a freshly-introduced schema will be empty until
            the worker redeploys.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {rows.map(row => (
              <ServiceHealthRow
                key={row.key}
                row={row}
                repoUrl={REPO_URL}
                webSha={WEB_VERSION.sha}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function ServiceHealthRow({
  row,
  repoUrl,
  webSha,
}: {
  row: ServiceHealthEntry;
  repoUrl: string;
  webSha: string;
}): React.JSX.Element {
  const matchesWeb =
    row.sha === webSha && row.sha !== 'unknown' && webSha !== 'unknown';
  const lastSeenMs = Date.parse(row.lastSeenAt);
  const stale =
    Number.isFinite(lastSeenMs) && Date.now() - lastSeenMs > 5 * 60 * 1000;
  const label = SERVICE_LABELS[row.key] ?? row.key;
  const compareUrl =
    row.sha !== 'unknown' && webSha !== 'unknown'
      ? `${repoUrl}/compare/${row.sha}...${webSha}`
      : null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto',
        alignItems: 'center',
        gap: 1.25,
        bgcolor: '#f8fafc',
        borderRadius: 1.5,
        px: 1.25,
        py: 0.75,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: stale ? '#e11d48' : matchesWeb ? '#10b981' : '#f59e0b',
          flexShrink: 0,
        }}
      />
      <Box sx={{minWidth: 0}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Typography sx={{fontSize: 12, fontWeight: 700, color: '#0f172a'}}>
            {label}
          </Typography>
          <Box
            component="a"
            href={`${repoUrl}/commit/${row.sha}`}
            target="_blank"
            rel="noreferrer"
            sx={{
              fontFamily: 'monospace',
              fontSize: 11,
              fontWeight: 700,
              color: '#4f46e5',
              textDecoration: 'none',
              '&:hover': {textDecoration: 'underline'},
            }}
          >
            {row.shortSha}
          </Box>
          {row.dirty ? (
            <Chip
              size="small"
              label="dirty"
              sx={{
                height: 16,
                fontSize: 9,
                fontWeight: 700,
                bgcolor: '#fee2e2',
                color: '#991b1b',
              }}
            />
          ) : null}
          {row.branch && row.branch !== 'main' && row.branch !== 'unknown' ? (
            <Chip
              size="small"
              label={row.branch}
              sx={{
                height: 16,
                fontSize: 9,
                fontWeight: 700,
                bgcolor: '#fef3c7',
                color: '#92400e',
              }}
            />
          ) : null}
        </Box>
        <Typography
          sx={{
            fontSize: 10,
            color: '#64748b',
            mt: 0.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.subject || '—'}
        </Typography>
        <Box sx={{display: 'flex', gap: 1.5, mt: 0.25}}>
          <Tooltip title={`Boot: ${row.bootedAt}`}>
            <Typography sx={{fontSize: 9, color: '#94a3b8'}}>
              up {humanizeTime(row.bootedAt)}
            </Typography>
          </Tooltip>
          <Tooltip title={`Last heartbeat: ${row.lastSeenAt}`}>
            <Typography
              sx={{fontSize: 9, color: stale ? '#e11d48' : '#94a3b8'}}
            >
              seen {humanizeTime(row.lastSeenAt)}
            </Typography>
          </Tooltip>
        </Box>
      </Box>
      {matchesWeb ? (
        <Chip
          size="small"
          label="matches UI"
          sx={{
            height: 20,
            fontSize: 9,
            fontWeight: 700,
            textTransform: 'uppercase',
            bgcolor: '#d1fae5',
            color: '#047857',
          }}
        />
      ) : compareUrl ? (
        <Chip
          size="small"
          component="a"
          href={compareUrl}
          target="_blank"
          rel="noreferrer"
          clickable
          label="behind UI · compare"
          sx={{
            height: 20,
            fontSize: 9,
            fontWeight: 700,
            textTransform: 'uppercase',
            bgcolor: '#fef3c7',
            color: '#92400e',
            cursor: 'pointer',
            '&:hover': {bgcolor: '#fde68a'},
          }}
        />
      ) : (
        <Chip
          size="small"
          label="unknown"
          sx={{
            height: 20,
            fontSize: 9,
            fontWeight: 700,
            bgcolor: '#f1f5f9',
            color: '#64748b',
          }}
        />
      )}
      {stale ? (
        <Chip
          size="small"
          label="stale"
          sx={{
            height: 20,
            fontSize: 9,
            fontWeight: 700,
            textTransform: 'uppercase',
            bgcolor: '#fee2e2',
            color: '#991b1b',
          }}
        />
      ) : (
        <Box />
      )}
    </Box>
  );
}
